import React from "react";
import { createPortal } from "react-dom";

interface DiscardChangesDialogProps {
  isOpen: boolean;
  onDiscard: () => void;
  onContinueEditing: () => void;
  title?: string;
  message?: string;
  discardLabel?: string;
  continueLabel?: string;
  backgroundColor?: string;
  borderColor?: string;
  boxShadow?: string;
  primaryTextColor?: string;
  secondaryTextColor?: string;
  accentColor?: string;
  fontFamily?: string;
}

export function DiscardChangesDialog({
  isOpen,
  onDiscard,
  onContinueEditing,
  title = "Discard unsaved changes?",
  message = "You have unsaved edits in this modal. Discard them and close?",
  discardLabel = "Discard",
  continueLabel = "Keep editing",
  backgroundColor = "rgba(255, 255, 255, 0.06)",
  borderColor = "rgba(255, 255, 255, 0.1)",
  boxShadow = "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
  primaryTextColor = "rgba(255, 255, 255, 0.95)",
  secondaryTextColor = "rgba(255, 255, 255, 0.7)",
  accentColor = "rgb(56, 189, 248)",
  fontFamily = "Arial, sans-serif",
}: DiscardChangesDialogProps) {
  if (!isOpen) return null;

  return createPortal(
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 230,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(15, 17, 21, 0.65)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        padding: "1.5rem",
      }}
      onClick={onContinueEditing}
      role="presentation"
      data-oid="pc3f.i2"
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="discard-dialog-title"
        aria-describedby="discard-dialog-description"
        onClick={(event) => event.stopPropagation()}
        style={{
          width: "min(420px, 90vw)",
          borderRadius: "1.5rem",
          border: `1px solid ${borderColor}`,
          background: backgroundColor,
          boxShadow,
          padding: "1.5rem",
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
          fontFamily,
        }}
        data-oid="ouwmi6v"
      >
        <div data-oid="ejw97uv">
          <h3
            id="discard-dialog-title"
            style={{
              margin: 0,
              fontSize: "1rem",
              fontWeight: 700,
              color: primaryTextColor,
            }}
            data-oid="mwkhil2"
          >
            {title}
          </h3>
          <p
            id="discard-dialog-description"
            style={{
              margin: "0.5rem 0 0 0",
              fontSize: "0.875rem",
              color: secondaryTextColor,
            }}
            data-oid="3x8ymjt"
          >
            {message}
          </p>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "0.75rem",
          }}
          data-oid="bnm3ytr"
        >
          <button
            type="button"
            onClick={onContinueEditing}
            style={{
              borderRadius: "9999px",
              border: `1px solid ${borderColor}`,
              background: "transparent",
              color: primaryTextColor,
              padding: "0.75rem 1rem",
              fontWeight: 600,
              fontFamily,
              cursor: "pointer",
            }}
            data-oid="d1jv.j."
          >
            {continueLabel}
          </button>
          <button
            type="button"
            onClick={onDiscard}
            style={{
              borderRadius: "9999px",
              border: "none",
              background: accentColor,
              color: "#ffffff",
              padding: "0.75rem 1rem",
              fontWeight: 700,
              fontFamily,
              cursor: "pointer",
            }}
            data-oid=":6s7r91"
          >
            {discardLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
